/* HUENIT LAB 2.0 — Admin: 예제 분류(카테고리 + 필터 태그) 관리.
   ★ 정본 = S3 official/taxonomy.json (26.07.03-admin §3). 마운트 시 게시본을 불러와
   localStorage 를 동기화하고, 편집하면 자동으로 다시 게시한다(디바운스). 앱은 이
   파일을 라벨 해석 최우선 출처로 소비 — 예제 0개 카테고리도 필터 칩에 노출된다. */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '../../icons/Icon.jsx';
import { Modal, Confirm, MLText, ColorPicker, Seg, useDragSort, useToast, Spinner } from '../../components/index.js';
import {
  useLocal, move, uid, LANG_SEED,
  SEED_EX_CATS, SEED_EX_FILTERS, SEED_ENGRAVE_CATS, SEED_ENGRAVE_FILTERS,
} from '../../data/store.js';
import { mlGet } from '../../lib/ml.js';
import { PALETTE } from '../../config/palette.js';
import { useSession } from '../../auth/session.jsx';
import { loadTaxonomy, publishTaxonomy } from './taxonomyRepo.js';

/* ============ 분류 행 편집 모달 ============ */
function TaxoModal({ kind, draft, setDraft, langs, onSave, onClose, isNew }) {
  const def = (langs.find(l => l.default) || langs[0]).code;
  const canSave = (draft.label[def] || '').trim();
  return (
    <Modal title={(isNew ? '추가' : '수정') + ' — ' + (kind === 'cat' ? '카테고리' : '필터 태그')} onClose={onClose}
      foot={<>
        <button className="btn btn--ghost" onClick={onClose}>취소</button>
        <button className="btn btn--primary" disabled={!canSave} onClick={onSave}>저장</button>
      </>}>
      <div className="field">
        <label className="field__label">이름<span className="req">*</span></label>
        <MLText value={draft.label} onChange={v => setDraft({ ...draft, label: v })} langs={langs} placeholder={kind === 'cat' ? '카테고리 이름' : '필터 이름'} />
      </div>
      {kind === 'cat' && (
        <div className="field">
          <label className="field__label">색상</label>
          <ColorPicker value={draft.color} onChange={c => setDraft({ ...draft, color: c })} />
        </div>
      )}
    </Modal>
  );
}

/* ============ 예제 분류 관리 (카테고리 + 필터) ============ */
export function TaxonomyAdmin() {
  const toast = useToast();
  const { getCredentials } = useSession();
  const [langs] = useLocal('adm.langs', LANG_SEED);
  const [cats, setCats] = useLocal('adm.exCats', SEED_EX_CATS);
  const [filters, setFilters] = useLocal('adm.exFilters', SEED_EX_FILTERS);
  // 그림·툴패스는 완전히 별도 축(코드 카테고리를 공유하지 않는다).
  const [engCats, setEngCats] = useLocal('adm.engraveCats', SEED_ENGRAVE_CATS);
  const [engFilters, setEngFilters] = useLocal('adm.engraveFilters', SEED_ENGRAVE_FILTERS);

  const def = (langs.find(l => l.default) || langs[0]).code;
  const [domain, setDomain] = useState('code');   // code | engrave
  const [edit, setEdit] = useState(null); // {kind, id|'new'}
  const [draft, setDraft] = useState(null);
  const [del, setDel] = useState(null);

  /* ---- S3 정본 동기: 마운트 시 게시본 로드 → 편집하면 디바운스 자동 게시 ---- */
  const [loaded, setLoaded] = useState(false);      // 게시본 로드 완료 전엔 게시 금지(정본 덮어쓰기 방지)
  const [pubState, setPubState] = useState(null);   // null | {state:'saving'|'saved'|'error', at?}
  const lastPubRef = useRef('');                    // 마지막 게시/로드 스냅샷(JSON)
  const snap = JSON.stringify({ cats, filters, engCats, engFilters });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pub = await loadTaxonomy();
        if (cancelled) return;
        if (pub) {
          // engrave 축이 게시본에 아직 없으면(null) [] 로 스냅샷 → 아래 이펙트가 로컬 시드를
          // 첫 게시한다(부트스트랩). 있으면(빈 배열 포함) 그대로 정본으로 수렴.
          lastPubRef.current = JSON.stringify({
            cats: pub.cats, filters: pub.filters,
            engCats: pub.engraveCats || [], engFilters: pub.engraveFilters || [],
          });
          setCats(pub.cats); setFilters(pub.filters);   // S3 = 정본(다른 PC 편집분 수렴)
          if (pub.engraveCats) setEngCats(pub.engraveCats);
          if (pub.engraveFilters) setEngFilters(pub.engraveFilters);
          setPubState({ state: 'saved', at: pub.publishedAt });
        }
        // 미게시(pub=null)면 lastPubRef='' 유지 → 아래 이펙트가 현재 로컬 분류를 첫 게시(부트스트랩)
      } catch (e) {
        if (!cancelled) toast('게시된 분류를 불러오지 못했습니다 — 로컬 데이터로 편집합니다.', 'error');
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!loaded) return;                               // 로드 완료 전 게시 금지
    if (snap === lastPubRef.current) return;
    setPubState({ state: 'saving' });
    const t = setTimeout(async () => {
      try {
        const at = await publishTaxonomy(getCredentials, {
          cats, filters, engraveCats: engCats, engraveFilters: engFilters,
        });
        lastPubRef.current = snap;
        setPubState({ state: 'saved', at });
      } catch (e) {
        setPubState({ state: 'error' });
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [loaded, snap, getCredentials]);   // eslint-disable-line react-hooks/exhaustive-deps

  // 훅은 조건부로 호출할 수 없으므로 4개를 모두 만들고 활성 축의 것만 쓴다.
  const catBind = useDragSort((f, t) => setCats(move(cats, f, t)));
  const filBind = useDragSort((f, t) => setFilters(move(filters, f, t)));
  const engCatBind = useDragSort((f, t) => setEngCats(move(engCats, f, t)));
  const engFilBind = useDragSort((f, t) => setEngFilters(move(engFilters, f, t)));

  /* 활성 축(코드 예제 / 그림·툴패스) — 아래 편집 동작은 전부 이걸 통해 나간다. */
  const isCode = domain === 'code';
  const aCats = isCode ? cats : engCats;
  const aFilters = isCode ? filters : engFilters;
  const setACats = isCode ? setCats : setEngCats;
  const setAFilters = isCode ? setFilters : setEngFilters;
  const aCatBind = isCode ? catBind : engCatBind;
  const aFilBind = isCode ? filBind : engFilBind;

  const openNew = (kind) => {
    setDraft(kind === 'cat'
      ? { id: uid(isCode ? 'c' : 'ec'), color: PALETTE[aCats.length % PALETTE.length], label: {} }
      : { id: uid(isCode ? 'f' : 'ef'), label: {} });
    setEdit({ kind, id: 'new' });
  };
  const openEdit = (kind, item) => { setDraft(JSON.parse(JSON.stringify(item))); setEdit({ kind, id: item.id }); };
  const save = () => {
    const { kind, id } = edit;
    const arr = kind === 'cat' ? aCats : aFilters; const setArr = kind === 'cat' ? setACats : setAFilters;
    if (id === 'new') setArr([...arr, draft]); else setArr(arr.map(x => x.id === id ? draft : x));
    toast(kind === 'cat' ? '카테고리를 저장했습니다' : '필터를 저장했습니다');
    setEdit(null); setDraft(null);
  };
  const remove = () => {
    const { kind, item } = del;
    if (kind === 'cat') setACats(aCats.filter(c => c.id !== item.id));
    else setAFilters(aFilters.filter(f => f.id !== item.id));
    setDel(null); toast('삭제했습니다');
  };

  const Section = ({ kind, items, bind, title, sub }) => (
    <div className="panel" style={{ marginBottom: 22 }}>
      <div className="panel__head">
        <div><div className="panel__title">{title}</div><div className="panel__sub">{sub}</div></div>
        <button className="btn btn--soft btn--sm" style={{ marginLeft: 'auto' }} onClick={() => openNew(kind)}><Icon name="plus" size={14} stroke={2.2} />추가</button>
      </div>
      <div className="panel__body">
        {items.length === 0 ? <div style={{ fontSize: 13, color: 'var(--ink-400)', padding: '8px 2px' }}>아직 없습니다.</div> : (
          <div className="rows">
            {items.map((it, i) => {
              const d = bind(i);
              return (
                <div key={it.id} className={"row" + (d.isDragging ? " is-dragging" : "") + (d.isOver ? " is-dragover" : "")}
                     onDragOver={d.onDragOver} onDragLeave={d.onDragLeave} onDrop={d.onDrop}>
                  <span className="row__handle" draggable={d.draggable} onDragStart={d.onDragStart} onDragEnd={d.onDragEnd}><Icon name="menu" size={16} /></span>
                  {kind === 'cat' && <span className="row__swatch" style={{ background: it.color }}></span>}
                  <div className="row__main">
                    <div className="row__title">{mlGet(it.label, def)}</div>
                    <div className="row__sub">{langs.filter(l => (it.label[l.code] || '').trim()).map(l => l.code).join(' · ') || '번역 없음'}</div>
                  </div>
                  <div className="row__actions">
                    <button className="icon-btn" onClick={() => openEdit(kind, it)} title="수정"><Icon name="file" size={15} /></button>
                    <button className="icon-btn icon-btn--danger" onClick={() => setDel({ kind, item: it })} title="삭제"><Icon name="trash" size={15} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Seg value={domain} onChange={setDomain} options={[
          { value: 'code', label: `코드 예제 (${cats.length}·${filters.length})`, icon: 'blocks' },
          { value: 'engrave', label: `그림·툴패스 (${engCats.length}·${engFilters.length})`, icon: 'image' },
        ]} />
        <div className="field__hint" style={{ marginTop: 6 }}>
          {isCode
            ? '블록 코딩·Python 예제에 쓰는 분류입니다.'
            : '그림·툴패스 예제 전용 분류입니다 — 코드 예제와 서로 섞이지 않습니다.'}
        </div>
      </div>

      <div className="toolbar">
        <div style={{ flex: 1, fontSize: 13, color: 'var(--ink-500)', fontWeight: 500 }}>
          분류는 수정하면 자동으로 서버(official/taxonomy.json)에 게시됩니다 — 앱 필터/라벨의 정본.
        </div>
        {pubState && pubState.state === 'saving' && (
          <span className="chip" style={{ background: 'var(--ink-100)', color: 'var(--ink-500)' }}><Spinner size={12} stroke={2} />게시 중…</span>
        )}
        {pubState && pubState.state === 'saved' && (
          <span className="chip" style={{ background: 'var(--ok-bg)', color: 'var(--ok)' }}><Icon name="check" size={12} />게시됨</span>
        )}
        {pubState && pubState.state === 'error' && (
          <span className="chip" style={{ background: 'var(--warn-bg)', color: 'var(--warn)' }} title="네트워크/권한을 확인하세요. 편집을 계속하면 다시 시도합니다."><Icon name="bell" size={12} />게시 실패</span>
        )}
      </div>
      <Section kind="cat" items={aCats} bind={aCatBind}
        title={isCode ? '코드 예제 카테고리' : '그림·툴패스 카테고리'} sub="색상으로 구분 · 드래그로 순서 변경" />
      <Section kind="filter" items={aFilters} bind={aFilBind}
        title={isCode ? '코드 예제 필터 태그' : '그림·툴패스 필터 태그'} sub="예제에 여러 개 붙일 수 있는 태그" />
      {edit && draft && <TaxoModal kind={edit.kind} draft={draft} setDraft={setDraft} langs={langs} isNew={edit.id === 'new'} onSave={save} onClose={() => { setEdit(null); setDraft(null); }} />}
      {del && <Confirm title="삭제할까요?" body={<><b>{mlGet(del.item.label, def)}</b> 항목을 삭제합니다.</>} onConfirm={remove} onClose={() => setDel(null)} />}
    </div>
  );
}

export default TaxonomyAdmin;
